import React, { useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getTopBreakdownItems } from 'utils/apiRequests';
import Investigate from './Investigate';
import {
	CATEGORY_BY_TAXONOMY,
	convertToArray,
	SPEND_TYPE,
	CATEGORY_TYPE,
	SPEND_BY_PROPS,
	BREAKDOWN_BY,
	getCurrencySymbolFromOptions,
	getCategoryParam,
} from './utils';
import isEqual from 'lodash/isEqual';

export class InvestigateContainer extends React.Component {
	state = {
		apiParams: null,
		topBreakdownItems: null,
	};

	componentDidUpdate(prevProps) {
		const { queryParams } = this.props;

		if (!isEqual(queryParams, prevProps.queryParams)) {
			this.fetchTopBreakdownItems();
		}
	}

	componentDidMount() {
		this.fetchTopBreakdownItems();
		this.setInitialQueryStringState();
	}

	setInitialQueryStringState = () => {
		const { queryParams, setQueryStringStateAndReplace } = this.props;

		setQueryStringStateAndReplace(queryParams);
	};

	fetchTopBreakdownItems = () => {
		const { queryParams } = this.props;
		const { spendType, splitSpendByDimension } = queryParams;
		const apiParams = this.getApiParams();
		// Do not fetch top breakdown items on root level and with no breakdown
		if (spendType != null && splitSpendByDimension !== BREAKDOWN_BY.NONE) {
			getTopBreakdownItems(apiParams).then(topBreakdownItems => {
				this.setState({
					topBreakdownItems,
					apiParams: {
						...apiParams,
						pivotingFilters: topBreakdownItems.map(item => item.id),
					},
				});
			});
		} else {
			this.setState({
				topBreakdownItems: null,
				apiParams: { ...apiParams, pivotingFilters: null },
			});
		}
	};

	getCategoryParam() {
		const { queryParams } = this.props;
		const { spendType, indirectTaxonomy, directTaxonomy } = queryParams;

		if (spendType == null) {
			return CATEGORY_TYPE.DEFAULT;
		}

		const taxonomy =
			spendType === SPEND_TYPE.DIRECT ? directTaxonomy : indirectTaxonomy;

		return CATEGORY_BY_TAXONOMY[taxonomy] || CATEGORY_TYPE.DEFAULT;
	}

	getApiParams() {
		const { queryParams } = this.props;
		const {
			showNonSourceable,
			splitSpendByDimension,
			dateFrom,
			dateTo,
			customFilters,
			customFiltersValues,
			hierarchy,
			currency,
			indirectTaxonomy: indirectTaxonomyType,
			directTaxonomy: directTaxonomyType,
			selectedItem,
			spendType,
			spendBy,
			storedQueryId,
			includeTailSpend,
			tailSpendLevel,
		} = queryParams;
		const dimensionParam =
			spendBy === SPEND_BY_PROPS.CATEGORY
				? getCategoryParam({
						spendType,
						indirectTaxonomy: indirectTaxonomyType,
						directTaxonomy: directTaxonomyType,
				  })
				: spendBy;
		const dimensions =
			spendBy === SPEND_BY_PROPS.CATEGORY
				? Array(Math.min(hierarchy.length + 1, 4)).fill(dimensionParam)
				: dimensionParam;

		const isSpendType = spendType != null;
		let queryHierarchy = hierarchy;

		if (selectedItem && !queryHierarchy.includes(selectedItem)) {
			queryHierarchy.push(selectedItem);
		}

		const apiParams = {
			harmonized: true,
			dateFrom,
			dateTo,
			hierarchy: queryHierarchy,
			currency,
			currencyType: 'fixed',
			indirectTaxonomyType,
			directTaxonomyType,
			storedQueryId,
			includeTailSpend,
			getTailSpendOnly: false,
			calculateTailSpend: Boolean(includeTailSpend),
			filters: [
				...customFilters,
				'sourceable',
				...(isSpendType ? ['spendType'] : []),
			],
			filterValues: [
				...customFiltersValues,
				showNonSourceable !== true,
				...(isSpendType ? [spendType] : []),
			],
			dimensions,
			...(isSpendType ? { splitSpendByDimension } : {}),
		};

		if (tailSpendLevel != null) {
			apiParams.getTailSpendOnly = true;
		}

		return apiParams;
	}

	render() {
		const { apiParams, topBreakdownItems } = this.state;

		return (
			<Investigate
				{...this.props}
				apiParams={apiParams}
				topBreakdownItems={topBreakdownItems}
			/>
		);
	}
}

export default props => {
	const {
		showNonSourceable,
		splitSpendByDimension,
		dateFrom,
		dateTo,
		customFilters,
		customFiltersValues,
		hierarchy,
		currency,
		indirectTaxonomy,
		directTaxonomy,
		spendType,
		spendBy,
		setQueryStringStateAndPush,
		setQueryStringStateAndReplace,
		storedQueryId,
		includeTailSpend,
		tailSpendLevel,
		selectedItem,
	} = useContext(QueryStringContext);
	const { options } = useContext(SettingsOptionsContext);
	const { currency: currencyList } = options;
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const location = useLocation();
	const history = useHistory();

	const getNumberedParamValue = val => {
		if (Number.isInteger(Number(val))) {
			return Number(val);
		}
		return undefined;
	};

	let queryHierarchy = convertToArray(hierarchy);

	const queryParams = {
		showNonSourceable: showNonSourceable === 'true',
		includeTailSpend: includeTailSpend === 'true',
		customFilters: convertToArray(customFilters),
		customFiltersValues: convertToArray(customFiltersValues),
		hierarchy: queryHierarchy,
		// 0 || undefined => undefined; undefined || 0 => 0;
		indirectTaxonomy: getNumberedParamValue(indirectTaxonomy),
		directTaxonomy: getNumberedParamValue(directTaxonomy),
		spendType: getNumberedParamValue(spendType),
		storedQueryId,
		dateFrom,
		dateTo,
		currency,
		spendBy,
		splitSpendByDimension,
		tailSpendLevel,
		selectedItem,
	};

	return (
		<InvestigateContainer
			{...props}
			queryParams={queryParams}
			currencySymbol={currencySymbol}
			location={location}
			history={history}
			setQueryStringStateAndPush={setQueryStringStateAndPush}
			setQueryStringStateAndReplace={setQueryStringStateAndReplace}
		/>
	);
};
